import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getMockUsers, saveMockUsers, getSubscriptionPlans, getMockPayments, saveMockPayments, getMockNotifications, saveMockNotifications } from '../mock';
import { masterAPI, notificationsAPI } from '../services/api';
import { CheckCircle, XCircle, Clock, AlertCircle, Calendar, Edit, DollarSign, Users, Trash2, UserCircle, Eye, RefreshCw, Bell, Send, Megaphone, CreditCard, Filter, Building2, Wallet } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import PullToRefresh from '../components/PullToRefresh';

const SubscriptionManagement = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // All users including non-admins
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editSubDialogOpen, setEditSubDialogOpen] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [massNotifyDialogOpen, setMassNotifyDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [previousUserCount, setPreviousUserCount] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [customPlan, setCustomPlan] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [subscriptionEditData, setSubscriptionEditData] = useState({});
  const [notificationData, setNotificationData] = useState({ title: '', message: '' });
  const [massNotificationData, setMassNotificationData] = useState({ title: '', message: '', target: 'all' });
  const { toast } = useToast();
  const { loginAs } = useAuth();
  const navigate = useNavigate();
  const plans = getSubscriptionPlans();

  useEffect(() => {
    loadUsers();
    
    // Auto-refresh every 10 seconds for real-time sync across devices
    const intervalId = setInterval(() => {
      loadUsers();
    }, 10000); // 10 seconds (reduced from 30 for better real-time sync)
    
    return () => clearInterval(intervalId);
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const allUsersData = await masterAPI.getAllUsers();
      setAllUsers(allUsersData);
      const adminUsers = allUsersData.filter(u => u.role === 'admin');
      
      // Check for changes in user data (cross-device sync detection)
      if (previousUserCount !== null && adminUsers.length !== previousUserCount) {
        toast({
          title: '🔄 Data Synced!',
          description: `Subscription data updated from another device. ${Math.abs(adminUsers.length - previousUserCount)} ${adminUsers.length > previousUserCount ? 'new' : 'removed'} user(s).`,
          duration: 3000,
        });
      }
      
      // Check for status changes
      if (previousUserCount !== null) {
        const previousPendingCount = users.filter(u => u.subscription?.status === 'pending').length;
        const newPendingCount = adminUsers.filter(u => u.subscription?.status === 'pending').length;
        
        if (newPendingCount < previousPendingCount) {
          toast({
            title: '✅ Subscription Updated!',
            description: `${previousPendingCount - newPendingCount} subscription(s) approved/rejected from another device.`,
            duration: 3000,
          });
        } else if (newPendingCount > previousPendingCount) {
          toast({
            title: '🔔 New Request!',
            description: `${newPendingCount - previousPendingCount} new subscription request(s) received.`,
            duration: 3000,
          });
        }
      }
      
      setUsers(adminUsers);
      setPreviousUserCount(adminUsers.length);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load users from backend, using mock data:', error);
      // Fallback to mock data
      const allUsers = getMockUsers();
      const adminUsers = allUsers.filter(u => u.role === 'admin');
      setUsers(adminUsers);
      setPreviousUserCount(adminUsers.length);
      setLastUpdated(new Date());
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-green-500">Active</Badge>,
      pending: <Badge className="bg-yellow-500">Pending</Badge>,
      expired: <Badge className="bg-red-500">Expired</Badge>,
      rejected: <Badge className="bg-gray-500">Rejected</Badge>,
    };
    return badges[status] || <Badge className="bg-gray-400">No Subscription</Badge>;
  };

  // Map old plan IDs to new plan names
  const getPlanName = (planId) => {
    const planNameMap = {
      'PLAN_FREE_TRIAL': 'Trial Plan',
      'PLAN_TRIAL': 'Trial Plan',
      'PLAN_FREE': 'Trial Plan',
      'PLAN_BASIC': '1 Month Plan',
      'PLAN_1_MONTH': '1 Month Plan',
      'PLAN_PRO': '3 Months Plan',
      'PLAN_3_MONTHS': '3 Months Plan',
      'PLAN_6_MONTHS': '6 Months Plan',
      'PLAN_12_MONTHS': '12 Months Plan',
      'PLAN_ENTERPRISE': 'Lifetime Plan',
      'PLAN_LIFETIME': 'Lifetime Plan'
    };
    
    // First try direct match
    if (planNameMap[planId]) return planNameMap[planId];
    
    // Then try from plans array
    const plan = plans.find(p => p.id === planId);
    return plan?.name || planId || 'Unknown Plan';
  };

  // Get payment method display info
  const getPaymentMethodInfo = (subscription) => {
    if (!subscription) return null;
    
    const paymentMethod = subscription.paymentMethod;
    const planId = subscription.plan;
    const isTrial = planId?.includes('TRIAL') || planId?.includes('FREE') || paymentMethod === 'trial' || paymentMethod === 'free_trial';
    
    // For trial plans, show Free Trial badge
    if (isTrial && paymentMethod !== 'master_approved' && paymentMethod !== 'razorpay') {
      return { 
        label: 'Free Trial', 
        icon: null, 
        color: 'text-gray-600', 
        bgColor: 'bg-gray-100 dark:bg-secondary',
        description: 'Free Trial',
        showBadge: false // Don't show badge for simple free trials
      };
    }
    
    switch (paymentMethod) {
      case 'razorpay':
        return { 
          label: 'Razorpay', 
          icon: <CreditCard className="w-3 h-3" />, 
          color: 'text-blue-700', 
          bgColor: 'bg-blue-100',
          description: 'Paid via Razorpay',
          showBadge: true
        };
      case 'master_approved':
        return { 
          label: 'Direct Pay', 
          icon: <Building2 className="w-3 h-3" />, 
          color: 'text-green-700', 
          bgColor: 'bg-green-100',
          description: 'Direct Pay to Admin',
          showBadge: true
        };
      case 'manual':
        return { 
          label: 'Direct Pay', 
          icon: <Building2 className="w-3 h-3" />, 
          color: 'text-amber-700', 
          bgColor: 'bg-amber-100',
          description: 'Direct Bank Transfer',
          showBadge: true
        };
      default:
        if (subscription.status === 'active' && !isTrial) {
          return { 
            label: 'Manual', 
            icon: <Wallet className="w-3 h-3" />, 
            color: 'text-purple-700', 
            bgColor: 'bg-purple-100',
            description: 'Manually Activated',
            showBadge: true
          };
        }
        return null;
    }
  };

  const handleApprove = async (userId) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      const planId = user.subscription?.plan || 'PLAN_BASIC';
      const plan = plans.find(p => p.id === planId);
      
      // Calculate duration in days
      let durationInDays = plan.duration;
      if (plan.durationType === 'month') {
        durationInDays = plan.duration * 30;
      } else if (plan.durationType === 'year') {
        durationInDays = plan.duration * 365;
      }

      // Call backend API to approve subscription
      await masterAPI.approveSubscription(userId, planId, durationInDays);
      
      // Reload users to get updated data
      await loadUsers();
      
      toast({
        title: 'Success!',
        description: `Subscription approved for ${user.name}`,
      });
    } catch (error) {
      console.error('Failed to approve subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve subscription',
        variant: 'destructive'
      });
      
      // Fallback to mock data
      const allUsers = getMockUsers();
      const userIndex = allUsers.findIndex(u => u.id === userId);
      
      if (userIndex !== -1) {
        const user = allUsers[userIndex];
        const planId = user.subscription?.plan || 'PLAN_BASIC';
        const plan = plans.find(p => p.id === planId);
        
        const startDate = new Date();
        const expiryDate = new Date();
        
        if (plan.durationType === 'days') {
          expiryDate.setDate(expiryDate.getDate() + plan.duration);
        } else if (plan.durationType === 'month') {
          expiryDate.setMonth(expiryDate.getMonth() + plan.duration);
        } else if (plan.durationType === 'year') {
          expiryDate.setFullYear(expiryDate.getFullYear() + plan.duration);
        }

        allUsers[userIndex].subscription = {
          ...user.subscription,
          status: 'active',
          startDate: startDate.toISOString(),
          expiryDate: expiryDate.toISOString(),
          approvedBy: 'MASTER_001',
          approvedAt: new Date().toISOString()
        };

        saveMockUsers(allUsers);
        loadUsers();
      }
    }
  };

  const handleReject = async (userId) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      // Call backend API to reject subscription
      await masterAPI.rejectSubscription(userId, 'Subscription request rejected by master admin');
      
      // Reload users to get updated data
      await loadUsers();
      
      toast({
        title: 'Rejected',
        description: `Subscription rejected for ${user.name}`,
      });
    } catch (error) {
      console.error('Failed to reject subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject subscription',
        variant: 'destructive'
      });
      
      // Fallback to mock data
      const allUsers = getMockUsers();
      const userIndex = allUsers.findIndex(u => u.id === userId);
      
      if (userIndex !== -1) {
        const user = allUsers[userIndex];
        allUsers[userIndex].subscription = {
          ...user.subscription,
          status: 'rejected',
          rejectedBy: 'MASTER_001',
          rejectedAt: new Date().toISOString()
        };

        saveMockUsers(allUsers);
        
        // Add notification
        const notifications = getMockNotifications();
        notifications.push({
          id: `NOTIF_${Date.now()}`,
          userId: userId,
          type: 'subscription_rejected',
          title: 'Subscription Request Rejected',
          message: 'Your subscription request was not approved. Please contact support.',
          read: false,
          createdAt: new Date().toISOString()
        });
        saveMockNotifications(notifications);

        loadUsers();
      }
    }
  };

  const handleRevoke = (userId) => {
    const allUsers = getMockUsers();
    const userIndex = allUsers.findIndex(u => u.id === userId);
    
    if (userIndex !== -1) {
      const user = allUsers[userIndex];
      allUsers[userIndex].subscription = {
        ...user.subscription,
        status: 'expired',
        revokedBy: 'MASTER_001',
        revokedAt: new Date().toISOString()
      };

      saveMockUsers(allUsers);
      loadUsers();
      toast({
        title: 'Revoked',
        description: `Subscription revoked for ${user.name}`,
        variant: 'destructive'
      });
    }
  };

  const handleExtend = () => {
    if (!selectedUser) return;

    const allUsers = getMockUsers();
    const userIndex = allUsers.findIndex(u => u.id === selectedUser.id);
    
    if (userIndex !== -1) {
      const user = allUsers[userIndex];
      const currentExpiry = user.subscription?.expiryDate ? new Date(user.subscription.expiryDate) : new Date();
      const newExpiry = new Date(currentExpiry);
      newExpiry.setDate(newExpiry.getDate() + parseInt(extendDays));

      allUsers[userIndex].subscription = {
        ...user.subscription,
        status: 'active',
        expiryDate: newExpiry.toISOString(),
        extendedBy: 'MASTER_001',
        extendedAt: new Date().toISOString()
      };

      saveMockUsers(allUsers);
      
      // Add notification
      const notifications = getMockNotifications();
      notifications.push({
        id: `NOTIF_${Date.now()}`,
        userId: selectedUser.id,
        type: 'subscription_extended',
        title: 'Subscription Extended',
        message: `Your subscription has been extended by ${extendDays} days!`,
        read: false,
        createdAt: new Date().toISOString()
      });
      saveMockNotifications(notifications);

      loadUsers();
      setExtendDialogOpen(false);
      toast({
        title: 'Success!',
        description: `Subscription extended by ${extendDays} days`,
      });
    }
  };

  const handleDelete = () => {
    if (!selectedUser) return;

    const allUsers = getMockUsers();
    
    // Delete the admin and all their staff
    const updatedUsers = allUsers.filter(u => 
      u.id !== selectedUser.id && u.adminId !== selectedUser.id
    );
    
    saveMockUsers(updatedUsers);
    loadUsers();
    setDeleteDialogOpen(false);
    
    toast({
      title: 'Deleted',
      description: `${selectedUser.name} and their staff have been deleted`,
      variant: 'destructive'
    });
  };

  const handleEdit = () => {
    if (!selectedUser) return;

    const allUsers = getMockUsers();
    const userIndex = allUsers.findIndex(u => u.id === selectedUser.id);
    
    if (userIndex !== -1) {
      allUsers[userIndex] = {
        ...allUsers[userIndex],
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone,
        venueName: editFormData.venueName
      };
      
      saveMockUsers(allUsers);
      loadUsers();
      setEditDialogOpen(false);
      
      toast({
        title: 'Success!',
        description: 'User details updated successfully',
      });
    }
  };

  const handleEditSubscription = async () => {
    if (!selectedUser) return;

    try {
      // Prepare subscription data
      const subscriptionData = {
        plan: subscriptionEditData.plan,
        startDate: subscriptionEditData.startDate,
        expiryDate: subscriptionEditData.expiryDate,
        status: subscriptionEditData.status,
        modifiedBy: 'MASTER',
        modifiedAt: new Date().toISOString()
      };

      // Update via backend API
      await masterAPI.updateUser(selectedUser.id, {
        subscription: subscriptionData
      });

      // Reload users to reflect changes
      await loadUsers();
      setEditSubDialogOpen(false);
      
      toast({
        title: 'Success!',
        description: 'Subscription details updated successfully',
      });
    } catch (error) {
      console.error('Failed to update subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update subscription',
        variant: 'destructive'
      });
    }
  };

  const handleLoginAs = async (userId) => {
    const result = await loginAs(userId);
    if (result.success) {
      toast({
        title: 'Logged in as user',
        description: `You are now viewing as ${result.user.name}`,
      });
      navigate('/dashboard');
    } else {
      toast({
        title: 'Error',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  // Send notification to specific user
  const handleSendNotification = async () => {
    if (!notificationData.title.trim() || !notificationData.message.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter both title and message',
        variant: 'destructive'
      });
      return;
    }

    try {
      await notificationsAPI.sendToUser(
        selectedUser.id,
        notificationData.title,
        notificationData.message
      );
      
      toast({
        title: '✅ Notification Sent!',
        description: `Message sent to ${selectedUser.name}`,
      });
      
      setNotifyDialogOpen(false);
      setNotificationData({ title: '', message: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send notification',
        variant: 'destructive'
      });
    }
  };

  // Send mass notification
  const handleSendMassNotification = async () => {
    if (!massNotificationData.title.trim() || !massNotificationData.message.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter both title and message',
        variant: 'destructive'
      });
      return;
    }

    try {
      const result = await notificationsAPI.sendMass(
        massNotificationData.title,
        massNotificationData.message,
        massNotificationData.target
      );
      
      toast({
        title: '📢 Mass Notification Sent!',
        description: result.message,
      });
      
      setMassNotifyDialogOpen(false);
      setMassNotificationData({ title: '', message: '', target: 'all' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send mass notification',
        variant: 'destructive'
      });
    }
  };

  const getStats = () => {
    return {
      total: users.length,
      active: users.filter(u => u.subscription?.status === 'active').length,
      pending: users.filter(u => u.subscription?.status === 'pending').length,
      expired: users.filter(u => u.subscription?.status === 'expired').length,
    };
  };

  const stats = getStats();

  // Filtered users based on all filters
  const filteredUsers = useMemo(() => {
    let result = [...users];
    
    // Status filter
    if (filterStatus !== 'all') {
      result = result.filter(u => u.subscription?.status === filterStatus);
    }
    
    // Plan filter
    if (filterPlan !== 'all') {
      if (filterPlan === 'none') {
        result = result.filter(u => !u.subscription?.plan);
      } else {
        result = result.filter(u => u.subscription?.plan === filterPlan);
      }
    }
    
    // Date range filter
    if (dateRangeFilter !== 'all') {
      const now = new Date();
      let startDate, endDate;
      
      if (dateRangeFilter === 'thisMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (dateRangeFilter === 'lastMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      } else if (dateRangeFilter === 'last3Months') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        endDate = now;
      } else if (dateRangeFilter === 'last6Months') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        endDate = now;
      } else if (dateRangeFilter === 'thisYear') {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = now;
      }
      
      if (startDate && endDate) {
        result = result.filter(u => {
          if (!u.createdAt) return false;
          const createdAt = new Date(u.createdAt);
          return createdAt >= startDate && createdAt <= endDate;
        });
      }
    }
    
    return result;
  }, [users, filterStatus, filterPlan, dateRangeFilter]);

  return (
    <PullToRefresh onRefresh={loadUsers}>
    <div className="p-3 sm:p-6 space-y-3 sm:space-y-6 min-h-screen overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground">Subscriptions</h1>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
            <p className="text-xs sm:text-sm text-gray-600">Manage subscriptions & approvals</p>
            {lastUpdated && (
              <div className="flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full w-fit">
                <RefreshCw className="w-2.5 h-2.5 flex-shrink-0" />
                <span>{lastUpdated.toLocaleTimeString()}</span>
                <span className="text-green-600 font-medium hidden sm:inline">• Auto</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button 
            onClick={() => setMassNotifyDialogOpen(true)}
            size="sm"
            className="flex items-center gap-1 flex-1 sm:flex-none text-xs sm:text-sm bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            <Megaphone className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Broadcast</span>
          </Button>
          <Button 
            onClick={loadUsers}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 flex-1 sm:flex-none text-xs sm:text-sm"
            disabled={isLoading}
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{isLoading ? 'Loading...' : 'Refresh'}</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-gray-500">Total</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-foreground">{stats.total}</p>
              </div>
              <Users className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-green-600">Active</p>
                <p className="text-lg sm:text-2xl font-bold text-green-700">{stats.active}</p>
              </div>
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-amber-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-yellow-600">Pending</p>
                <p className="text-lg sm:text-2xl font-bold text-yellow-700">{stats.pending}</p>
              </div>
              <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-rose-100">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-red-600">Expired</p>
                <p className="text-lg sm:text-2xl font-bold text-red-700">{stats.expired}</p>
              </div>
              <AlertCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User List with Filters */}
      <Card>
        <CardHeader className="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-sm sm:text-base dark:text-foreground">All Subscriptions ({filteredUsers.length})</CardTitle>
            </div>
            
            {/* Filter Row */}
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">Filter:</span>
              
              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-28 sm:w-32 h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="no_subscription">No Plan</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Subscription Type Filter */}
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-32 sm:w-36 h-8 text-xs">
                  <SelectValue placeholder="Plan Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="PLAN_FREE_TRIAL">Free Trial</SelectItem>
                  <SelectItem value="PLAN_1_MONTH">1 Month</SelectItem>
                  <SelectItem value="PLAN_3_MONTHS">3 Months</SelectItem>
                  <SelectItem value="PLAN_6_MONTHS">6 Months</SelectItem>
                  <SelectItem value="PLAN_12_MONTHS">12 Months</SelectItem>
                  <SelectItem value="PLAN_LIFETIME">Lifetime</SelectItem>
                  <SelectItem value="none">No Plan</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Registration Month Filter */}
              <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                <SelectTrigger className="w-36 sm:w-40 h-8 text-xs">
                  <SelectValue placeholder="Registration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                  <SelectItem value="last3Months">Last 3 Months</SelectItem>
                  <SelectItem value="last6Months">Last 6 Months</SelectItem>
                  <SelectItem value="thisYear">This Year</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Clear Filters */}
              {(filterStatus !== 'all' || filterPlan !== 'all' || dateRangeFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterStatus('all');
                    setFilterPlan('all');
                    setDateRangeFilter('all');
                  }}
                  className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </Button>
              )}
            </div>
            
            {/* Filter Results Summary */}
            {(filterStatus !== 'all' || filterPlan !== 'all' || dateRangeFilter !== 'all') && (
              <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                <Filter className="w-3 h-3 text-blue-500" />
                <span>Showing {filteredUsers.length} of {users.length} subscribers</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          <div className="space-y-2 sm:space-y-3">
            {filteredUsers.map((user) => {
              const plan = plans.find(p => p.id === user.subscription?.plan);
              const expiryDate = user.subscription?.expiryDate ? new Date(user.subscription.expiryDate) : null;
              const daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

              return (
                <Card key={user.id} className="border">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-sm sm:text-base truncate">{user.name}</h3>
                          {getStatusBadge(user.subscription?.status || 'no_subscription')}
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 truncate">{user.email}</p>
                        <p className="text-[11px] sm:text-xs text-gray-500">{user.phone}</p>
                        {user.venueName && <p className="text-[11px] sm:text-xs text-gray-500 truncate">Venue: {user.venueName}</p>}
                        
                        {user.subscription && (
                          <div className="mt-2 space-y-1.5">
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] sm:text-xs">
                              <span><b>Plan:</b> {getPlanName(user.subscription?.plan)}</span>
                              {expiryDate && (
                                <span>
                                  <b>Expires:</b> {expiryDate.toLocaleDateString('en-IN')}
                                  {daysLeft !== null && daysLeft > 0 && (
                                    <span className={`ml-1 ${daysLeft < 7 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                      ({daysLeft}d)
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            {/* Payment Method Info */}
                            {(() => {
                              const paymentInfo = getPaymentMethodInfo(user.subscription);
                              if (paymentInfo && paymentInfo.showBadge) {
                                return (
                                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${paymentInfo.bgColor}`}>
                                    {paymentInfo.icon}
                                    <span className={`text-[10px] sm:text-xs font-medium ${paymentInfo.color}`}>
                                      {paymentInfo.description || paymentInfo.label}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap lg:flex-col gap-1.5 sm:gap-2 w-full lg:w-auto">
                        {/* Login As Button - Always visible */}
                        <Button
                          onClick={() => handleLoginAs(user.id)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Login As
                        </Button>

                        {/* Notify Button */}
                        <Button
                          onClick={() => {
                            setSelectedUser(user);
                            setNotificationData({ title: '', message: '' });
                            setNotifyDialogOpen(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                        >
                          <Bell className="w-3 h-3 mr-1" />
                          Notify
                        </Button>

                        {user.subscription?.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleApprove(user.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleReject(user.id)}
                              size="sm"
                              variant="destructive"
                              className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        
                        {user.subscription?.status === 'active' && (
                          <>
                            <Button
                              onClick={() => {
                                setSelectedUser(user);
                                setExtendDialogOpen(true);
                              }}
                              size="sm"
                              variant="outline"
                              className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              Extend
                            </Button>
                            <Button
                              onClick={() => handleRevoke(user.id)}
                              size="sm"
                              variant="destructive"
                              className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                            >
                              Revoke
                            </Button>
                          </>
                        )}
                        
                        {(user.subscription?.status === 'expired' || user.subscription?.status === 'rejected') && (
                          <Button
                            onClick={() => handleApprove(user.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                          >
                            Reactivate
                          </Button>
                        )}

                        {/* Edit Subscription Button */}
                        <Button
                          onClick={() => {
                            setSelectedUser(user);
                            setSubscriptionEditData({
                              plan: user.subscription?.plan || 'PLAN_BASIC',
                              startDate: user.subscription?.startDate || new Date().toISOString().split('T')[0],
                              expiryDate: user.subscription?.expiryDate || new Date().toISOString().split('T')[0],
                              status: user.subscription?.status || 'pending'
                            });
                            setEditSubDialogOpen(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                        >
                          <DollarSign className="w-3 h-3 mr-1" />
                          Edit Sub
                        </Button>

                        {/* Edit User Button */}
                        <Button
                          onClick={() => {
                            setSelectedUser(user);
                            setEditFormData({
                              name: user.name,
                              email: user.email,
                              phone: user.phone,
                              venueName: user.venueName
                            });
                            setEditDialogOpen(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>

                        {/* Delete Button */}
                        <Button
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          size="sm"
                          variant="destructive"
                          className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Del
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No users found with the selected filter.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Extend Subscription Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Subscription</DialogTitle>
            <DialogDescription>
              Extend subscription for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="days">Extend by (days)</Label>
              <Input
                id="days"
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                min="1"
                max="365"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setExtendDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleExtend} className="bg-teal-600 hover:bg-teal-700">
                Extend Subscription
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Details</DialogTitle>
            <DialogDescription>
              Update information for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name || ''}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editFormData.phone || ''}
                onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-venue">Venue Name</Label>
              <Input
                id="edit-venue"
                value={editFormData.venueName || ''}
                onChange={(e) => setEditFormData({...editFormData, venueName: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEdit} className="bg-teal-600 hover:bg-teal-700">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={editSubDialogOpen} onOpenChange={setEditSubDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subscription Details</DialogTitle>
            <DialogDescription>
              Modify subscription for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="sub-plan">Subscription Plan</Label>
              <Select
                value={subscriptionEditData.plan}
                onValueChange={(value) => setSubscriptionEditData({...subscriptionEditData, plan: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - {plan.currency}{plan.price.toLocaleString('en-IN')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sub-status">Status</Label>
              <Select
                value={subscriptionEditData.status}
                onValueChange={(value) => setSubscriptionEditData({...subscriptionEditData, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sub-start">Start Date</Label>
              <Input
                id="sub-start"
                type="date"
                value={subscriptionEditData.startDate ? subscriptionEditData.startDate.split('T')[0] : ''}
                onChange={(e) => setSubscriptionEditData({...subscriptionEditData, startDate: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="sub-expiry">Expiry Date</Label>
              <Input
                id="sub-expiry"
                type="date"
                value={subscriptionEditData.expiryDate ? subscriptionEditData.expiryDate.split('T')[0] : ''}
                onChange={(e) => setSubscriptionEditData({...subscriptionEditData, expiryDate: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditSubDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditSubscription} className="bg-teal-600 hover:bg-teal-700">
                Update Subscription
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This action cannot be undone. This will permanently delete the admin account and all associated staff members.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleDelete} variant="destructive">
                Delete Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Notification to User Dialog */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Send Notification
            </DialogTitle>
            <DialogDescription>
              Send a custom notification to <strong>{selectedUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="notify-title">Title *</Label>
              <Input
                id="notify-title"
                placeholder="Enter notification title"
                value={notificationData.title}
                onChange={(e) => setNotificationData({...notificationData, title: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="notify-message">Message *</Label>
              <Textarea
                id="notify-message"
                placeholder="Enter your message..."
                value={notificationData.message}
                onChange={(e) => setNotificationData({...notificationData, message: e.target.value})}
                className="mt-1 min-h-[100px]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendNotification} 
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Send className="w-4 h-4 mr-2" />
                Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mass Notification Dialog */}
      <Dialog open={massNotifyDialogOpen} onOpenChange={setMassNotifyDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-red-500" />
              Broadcast Notification
            </DialogTitle>
            <DialogDescription>
              Send a notification to multiple users at once
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="mass-target">Target Audience</Label>
              <Select
                value={massNotificationData.target}
                onValueChange={(value) => setMassNotificationData({...massNotificationData, target: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select target" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">📢 All Users</SelectItem>
                  <SelectItem value="admins">👔 Admins Only</SelectItem>
                  <SelectItem value="staff">👥 Staff Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="mass-title">Title *</Label>
              <Input
                id="mass-title"
                placeholder="Enter notification title"
                value={massNotificationData.title}
                onChange={(e) => setMassNotificationData({...massNotificationData, title: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="mass-message">Message *</Label>
              <Textarea
                id="mass-message"
                placeholder="Enter your broadcast message..."
                value={massNotificationData.message}
                onChange={(e) => setMassNotificationData({...massNotificationData, message: e.target.value})}
                className="mt-1 min-h-[120px]"
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>⚠️ Note:</strong> This will send a notification to {
                  massNotificationData.target === 'all' ? 'ALL users' :
                  massNotificationData.target === 'admins' ? 'all admin users' :
                  'all staff members'
                } in the system.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMassNotifyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendMassNotification} 
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Send Broadcast
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
};

export default SubscriptionManagement;
